import React from "react";
import {
  Home,
  Users,
  Package,
  Truck,
  Settings,
  LogOut,
  UserCheck,
  ChartBar,
  ShieldCheckIcon,
  Ticket,
  GitPullRequest,
  Bell,
  Package2,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import SidebarLink from "./SidebarLink";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { logout } from "../../redux/features/authSlice";
import { closeSidebar } from "../../redux/features/modalSlice";
import { persistor } from "../../redux/app/store";
import { resetCourier } from "../../redux/features/CourrierSlice";
import {
  isCourierOrAdmin,
  isCourierAdminOrSuper,
  isInvestorOrSuper,
  isSuperAdmin,
} from "../../helpers";

const Sidebar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user }: any = useAppSelector((state) => state.auth);

  const handleLinkClick = (reset: boolean = true) => {
    dispatch(closeSidebar());
    if (reset) dispatch(resetCourier());
  };

  const renderNavLink = (to: string, label: string, reset = true) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block p-2 !no-underline rounded hover:bg-bgHoverPrimary hover:text-white ${
          isActive
            ? "bg-bgHoverPrimary border-l-4 border-white !text-white"
            : "text-white"
        }`
      }
      onClick={() => handleLinkClick(reset)}
    >
      {label}
    </NavLink>
  );

  return (
    <div className="flex flex-col h-screen bg-primary text-white w-full gap-12 py-4 overflow-y-auto">
      {/* Logo */}
      <div className="flex items-center justify-center h-16">
        <img src="/assets/images/svg/logo.svg" alt="Logo" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-4">
        {/* Dashboard */}
        {isInvestorOrSuper(user) && (
          <SidebarLink icon={Home} label="Dashboard" to="/dashboard" />
        )}

        {/* User Management */}
        {isInvestorOrSuper(user) && (
          <>
            <SidebarLink icon={Users} label="User Management" to="#">
              {renderNavLink("/user-management/vendor", "Vendor")}
              {renderNavLink("/user-management/buyer", "Buyer")}
              {user?.user?.roles !== "INVESTOR" &&
                renderNavLink("/user-management/referred", "Referred")}
            </SidebarLink>
            <SidebarLink
              icon={Truck}
              label="Courier Management"
              to="/courier-management"
            />
          </>
        )}

        {/* Order Management */}
        {isCourierOrAdmin(user) && (
          <SidebarLink icon={Package} label="Order Management" to="#">
            {renderNavLink("/order-management/all-orders", "All Orders")}
            {renderNavLink(
              "/order-management/disputed-orders",
              "Disputed Orders"
            )}
            {renderNavLink(
              "/order-management/returned-orders",
              "Returned Orders"
            )}
            {renderNavLink(
              "/order-management/executed-orders",
              "Executed Orders",
              false
            )}
            {renderNavLink(
              "/order-management/orders-in-transit",
              "Orders In Transit",
              false
            )}
          </SidebarLink>
        )}

        {/* Courier & Agent */}
        {isCourierAdminOrSuper(user) && (
          <>
            <SidebarLink
              icon={UserCheck}
              label="Agent Management"
              to="/agent-management"
            />
          </>
        )}

        {/* Request Management */}
        {isInvestorOrSuper(user) && (
          <SidebarLink
            icon={GitPullRequest}
            label="Request Management"
            to="/request-management"
          />
        )}

        {/* Analytics */}
        {isSuperAdmin(user) && (
          <SidebarLink icon={ChartBar} label="Analytics" to="#">
            {renderNavLink("/analytics", "Overview", false)}
            {renderNavLink("/detailed-analytics", "Detailed Analytics", false)}
            {user?.user?.roles !== "INVESTOR" &&
              renderNavLink("/transactions", "Transactions", false)}
          </SidebarLink>
        )}

        {/* Super Admin Only */}
        {isSuperAdmin(user) && (
          <>
            <SidebarLink
              icon={Package2}
              label="Product Management"
              to="/product-management"
            />
            <SidebarLink
              icon={Ticket}
              label="Coupons Management"
              to="/coupons"
            />
            <SidebarLink
              icon={ShieldCheckIcon}
              label="KYC Management"
              to="/kyc-management"
            />
            <SidebarLink
              icon={UserCheck}
              label="Admin Management"
              to="/admin-management"
            />
            <SidebarLink
              icon={Bell}
              label="Notifications"
              to="/notifications"
            />
          </>
        )}
        {/* Settings */}
        <SidebarLink icon={Settings} label="Settings" to="/settings" />
      </nav>

      {/* Logout */}
      <div className="p-4">
        <button
          className="flex items-center space-x-3 p-2 rounded hover:bg-bgHoverPrimary text-white hover:text-white"
          onClick={() => {
            dispatch(logout());
            navigate("/");
            persistor.purge();
          }}
        >
          <LogOut />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
